import { buildSectionRenderingURL, sectionRenderer, morphSection } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Checks if the current viewport is mobile
 * @returns {boolean}
 */
const isMobile = () => window.innerWidth <= 640;

const getActiveFilterCount = (params) =>
  [...params.entries()].filter(([key]) => key.startsWith('filter.')).length;

const updateFilterButtonLabels = (params = new URLSearchParams(window.location.search)) => {
  const count = getActiveFilterCount(params);

  document.querySelectorAll('.filter_btn').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;

    const textElement = button.querySelector('.filter_btn-text') || button;
    const label = button.dataset.filterLabel || textElement.textContent?.trim() || '';
    if (!label) return;

    textElement.textContent = count > 0 ? `${label} (${count})` : label;
  });
};

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];
  #openFiltersKey = '';
  #suppressViewTransition = false;
  #desktopCompactStickyEnabled = false;
  /** @type {(() => void) | null} */
  #desktopScrollHandler = null;
  /** @type {(() => void) | null} */
  #desktopResizeHandler = null;
  #isDesktopFiltersCompact = false;
  #forceDesktopCompact = false;
  /** @type {URLSearchParams | null} */
  #pendingMobileFilters = null;
  /** @type {string | null} */
  #cachedSectionHtml = null;
  /** @type {string | null} */
  #cachedSectionParams = null;
  /** @type {AbortController | null} */
  #countUpdateAbortController = null;
  #loadMoreStateKey = 'mp-load-more-state';
  /** @type {(() => void) | null} */
  #scrollStateSaverHandler = null;
  #debouncedUpdateCountOnly = debounce(() => {
    this.#updateCountOnly();
  }, 300);

  connectedCallback() {
    super.connectedCallback();
    this.#openFiltersKey = `filters-open:${this.sectionId}`;
    if (!window.__catalogFiltersInitialised) {
      sessionStorage.removeItem(this.#openFiltersKey);
      window.__catalogFiltersInitialised = true;
    }
    this.#restoreOpenFilters();
    this.#attachShowResultsListener();
    this.#attachLoadMoreListener();
    this.#attachCollapseFiltersListener();
    this.#attachSquadronSearchListeners();
    this.#setupDesktopStickyFilters();
    this.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(window.location.search)));
    updateFilterButtonLabels();
    this.#restoreLoadMoreState();
    this.#attachScrollStateSaver();
  }

  disconnectedCallback() {
    this.#teardownDesktopStickyFilters();
    if (this.#scrollStateSaverHandler) {
      window.removeEventListener('scroll', this.#scrollStateSaverHandler);
      this.#scrollStateSaverHandler = null;
    }
    super.disconnectedCallback?.();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const currentParameters = new URLSearchParams(window.location.search);
    const newParameters = new URLSearchParams();
    const valuesByKey = new Map();

    // Collect filter values from checked inputs
    this.#collectFilterInputs(valuesByKey);

    // Collect non-filter values from FormData
    this.#collectFormDataValues(formData, valuesByKey);

    // Copy non-filter parameters from current URL (preserve sort_by, etc.)
    this.#copyNonFilterParameters(currentParameters, newParameters, valuesByKey);

    // Build final parameters
    this.#buildParameters(valuesByKey, newParameters);

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Copies non-filter parameters from current URL
   * @param {URLSearchParams} currentParameters - Current URL parameters
   * @param {URLSearchParams} newParameters - New parameters to populate
   */
  #copyNonFilterParameters(currentParameters, newParameters, valuesByKey) {
    for (const [key, value] of currentParameters.entries()) {
      if (key.startsWith('filter.') || key === 'page' || key === SEARCH_QUERY) continue;
      if (valuesByKey.has(key)) continue;
      if (newParameters.has(key)) continue;
      newParameters.set(key, value);
    }
  }

  /**
   * Collects filter values from form inputs
   * @param {Map<string, string[]>} valuesByKey - Map to store collected values
   */
  #collectFilterInputs(valuesByKey) {
    const form = this.refs.facetsForm;
    if (!form) return;

    // Collect checked checkboxes
    const checkedInputs = form.querySelectorAll(
      'input[type="checkbox"][name^="filter."]:checked, input[type="radio"][name^="filter."]:checked'
    );
    this.#addInputsToMap(checkedInputs, valuesByKey);

    // Collect text/number/range inputs
    const textInputs = form.querySelectorAll('input[type="text"][name^="filter."], input[type="number"][name^="filter."], input[type="range"][name^="filter."]');
    this.#addInputsToMap(textInputs, valuesByKey);
  }

  /**
   * Adds input values to the map
   * @param {NodeListOf<Element>} inputs - Input elements to process
   * @param {Map<string, string[]>} valuesByKey - Map to store values
   */
  #addInputsToMap(inputs, valuesByKey) {
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const key = input.name;
      const value = input.value;
      if (typeof value !== 'string' || value.trim() === '') continue;

      if (key.includes('filter.p.tag') || key.includes('filter.p.product_tag')) {
        valuesByKey.set(key, [value]);
        continue;
      }

      const existing = valuesByKey.get(key);
      if (existing) {
        existing.push(value);
      } else {
        valuesByKey.set(key, [value]);
      }
    }
  }

  /**
   * Collects non-filter values from FormData
   * @param {FormData} formData - Form data to process
   * @param {Map<string, string[]>} valuesByKey - Map to store values
   */
  #collectFormDataValues(formData, valuesByKey) {
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('filter.')) continue;
      if (typeof value !== 'string' || value.trim() === '') continue;

      valuesByKey.set(key, [value]);
    }
  }

  /**
   * Builds final URL parameters from collected values
   * @param {Map<string, string[]>} valuesByKey - Collected values
   * @param {URLSearchParams} newParameters - Parameters to populate
   */
  #buildParameters(valuesByKey, newParameters) {
    for (const [key, values] of valuesByKey.entries()) {
      newParameters.delete(key);
      
      if (values.length === 1 && !key.startsWith('filter.')) {
        const value = values[0];
        if (value) newParameters.set(key, value);
      } else {
        values.forEach((value) => newParameters.append(key, value));
      }
    }
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    updateFilterButtonLabels(this.createURLParameters());

    if (isMobile()) {
      // On mobile, only update count and store pending filters
      this.#debugCount('Queued mobile count update.', {
        params: this.#serializeParams(this.createURLParameters()),
      });
      this.#debouncedUpdateCountOnly();
    } else {
      // On desktop, update immediately as before
      this.#debouncedUpdateCountOnly.cancel();
      this.#abortPendingCountUpdate();
      this.#storeOpenFilters();
      this.#updateURLHash();
      this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
      this.#updateSection();
    }
  };

  /**
   * Updates only the result count on mobile without rendering the grid
   */
  async #updateCountOnly(urlParameters = this.createURLParameters()) {
    this.#pendingMobileFilters = urlParameters;
    this.#abortPendingCountUpdate();

    const abortController = new AbortController();
    this.#countUpdateAbortController = abortController;

    const sectionUrl = this.#buildSectionRenderURL(urlParameters);
    this.#debugCount('Fetching section for count update.', {
      url: sectionUrl,
      params: this.#serializeParams(urlParameters),
    });

    // Fetch section HTML to get updated count
    try {
      const response = await fetch(sectionUrl, { signal: abortController.signal });
      if (!response.ok) {
        console.warn('Failed to fetch section for count update.', {
          status: response.status,
          url: response.url,
        });
        return;
      }

      const html = await response.text();
      if (abortController.signal.aborted || this.#countUpdateAbortController !== abortController) return;

      // Cache the HTML for later use
      this.#cachedSectionHtml = html;
      this.#cachedSectionParams = this.#serializeParams(urlParameters);

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const result = this.#extractProductCount(doc);
      if (!result) {
        console.warn('Unable to parse product count from section response.', { url: sectionUrl });
        return;
      }

      this.#debugCount('Parsed product count.', {
        count: result.count,
        source: result.source,
        raw: result.raw,
      });

      // Update the "Show Results" button text
      this.#updateShowResultsButton(result.count);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Failed to update count:', error);
    } finally {
      if (this.#countUpdateAbortController === abortController) {
        this.#countUpdateAbortController = null;
      }
    }
  }

  #abortPendingCountUpdate() {
    if (this.#countUpdateAbortController) {
      this.#countUpdateAbortController.abort();
      this.#countUpdateAbortController = null;
      this.#debugCount('Aborted previous count update request.');
    }
  }

  #buildSectionRenderURL(urlParameters) {
    const url = new URL(window.location.href);
    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    return buildSectionRenderingURL(this.sectionId, url);
  }

  #extractProductCount(doc) {
    const paginationInfo = doc.querySelector('#paginationInfo');
    if (paginationInfo instanceof HTMLElement) {
      const totalItems =
        paginationInfo.dataset.totalItems || paginationInfo.getAttribute('data-total-items') || '';
      const parsed = this.#parseCountValue(totalItems);
      if (parsed !== null) {
        return { count: parsed, source: 'paginationInfo', raw: totalItems };
      }
    }

    const headerCount = doc.querySelector('.products__result-text');
    if (headerCount instanceof HTMLElement) {
      const text = headerCount.textContent || '';
      const parsed = this.#parseCountFromText(text);
      if (parsed !== null) {
        return { count: parsed, source: 'resultText', raw: text };
      }
    }

    return null;
  }

  #parseCountValue(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).replace(/[^\d]/g, '');
    if (!normalized) return null;
    const parsed = parseInt(normalized, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  #parseCountFromText(text) {
    if (!text) return null;
    const matches = text.match(/\d[\d\s.,\u00A0\u202F]*/g);
    if (!matches) return null;

    const numbers = matches
      .map((match) => match.replace(/[^\d]/g, ''))
      .filter(Boolean)
      .map((match) => parseInt(match, 10))
      .filter((value) => !Number.isNaN(value));

    if (!numbers.length) return null;
    return Math.max(...numbers);
  }

  #serializeParams(urlParameters) {
    const normalized = new URLSearchParams(urlParameters);
    normalized.sort();
    return normalized.toString();
  }

  #debugCount(message, data) {
    if (data) {
      console.log(`[facets] ${message}`, data);
      return;
    }
    console.log(`[facets] ${message}`);
  }

  #logAppliedFilters(context) {
    const url = new URL(window.location.href);
    console.log(`[facets] Applied filters (${context}).`, {
      url: url.toString(),
      params: url.searchParams.toString(),
    });

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) {
      console.log(`[facets] Section missing after apply (${context}).`);
      return;
    }

    const info = section.querySelector('#paginationInfo');
    const grid = section.querySelector('#productGrid');
    const cardCount = grid?.querySelectorAll('.product-card').length ?? 0;
    const totalItems =
      info instanceof HTMLElement
        ? info.dataset.totalItems || info.getAttribute('data-total-items') || null
        : null;
    const rangeStart = info instanceof HTMLElement ? info.dataset.rangeStart || null : null;
    const rangeEnd = info instanceof HTMLElement ? info.dataset.rangeEnd || null : null;

    console.log(`[facets] Rendered grid state (${context}).`, {
      cardCount,
      totalItems,
      rangeStart,
      rangeEnd,
    });
  }

  /**
   * Updates the Show Results button text with new count
   * @param {number} count - The number of results
   */
  #updateShowResultsButton(count) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;
    
    const button = section.querySelector('.filters__results');
    if (!(button instanceof HTMLElement)) return;
    
    // Get translation pattern from button's data attribute or use default
    const pattern = button.dataset.countPattern || 'Show [COUNT] results';
    button.textContent = pattern.replace('[COUNT]', String(count));
  }

  /**
   * Attaches click listener to Show Results button for mobile
   */
  #attachShowResultsListener() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;
    
    const button = section.querySelector('.filters__results');
    if (!button) return;
    
    // Remove any existing listener to prevent duplicates
    // @ts-ignore - Custom property for event listener reference
    const existingListener = button._showResultsListener;
    if (existingListener) {
      button.removeEventListener('click', existingListener);
    }
    
    // Create new listener
    const listener = async () => {
      if (!isMobile()) return;

      // Ensure we apply the latest form state even if no pending filters were stored.
      this.#debouncedUpdateCountOnly.cancel();
      this.#abortPendingCountUpdate();
      const pendingFilters = this.createURLParameters();
      const pendingKey = this.#serializeParams(pendingFilters);

      this.#pendingMobileFilters = pendingFilters;

      const hasMatchingCache = this.#cachedSectionParams === pendingKey;
      if (!hasMatchingCache) {
        this.#cachedSectionHtml = null;
        this.#cachedSectionParams = null;
        this.#debugCount('Cleared cached section HTML before apply.', { params: pendingKey });
        await this.#updateCountOnly(pendingFilters);
      }

      this.#applyMobileFilters();
    };
    
    // Store reference and attach
    // @ts-ignore - Custom property for event listener reference
    button._showResultsListener = listener;
    button.addEventListener('click', listener);
  }

  /**
   * Applies the pending mobile filters and updates the grid
   */
  #applyMobileFilters() {
    if (!this.#pendingMobileFilters) return;
    
    const pendingFilters = this.#pendingMobileFilters;
    this.#storeOpenFilters();
    
    // Update URL with pending filters
    const url = new URL(window.location.href);
    url.search = '';
    for (const [param, value] of pendingFilters.entries()) {
      url.searchParams.append(param, value);
    }
    history.pushState({ urlParameters: pendingFilters.toString() }, '', url.toString());
    
    this.dispatchEvent(new FilterUpdateEvent(pendingFilters));
    this.#pendingMobileFilters = null;
    this.#debugCount('Applying pending filters after click.', {
      params: this.#serializeParams(pendingFilters),
      cached: Boolean(this.#cachedSectionHtml),
    });
    
    // Use cached HTML if available, otherwise fetch
    if (this.#cachedSectionHtml) {
      this.#debugCount('Applying cached section HTML.');
      this.#updateSectionFromCache();
    } else {
      this.#debugCount('No cached HTML, rendering section.');
      this.#updateSection();
    }
    
    // Close mobile filter drawer
    this.#closeMobileFilters();
  }

  /**
   * Updates the section using cached HTML
   */
  async #updateSectionFromCache() {
    const cachedHtml = this.#cachedSectionHtml;
    if (!cachedHtml) {
      this.#updateSection();
      return;
    }
    
    const viewTransition = this.#shouldUseViewTransition();
    
    const updateDOM = async () => {
      // Use morphSection to properly update DOM and preserve event listeners
      await morphSection(this.sectionId, cachedHtml);
      
      // Clear cached HTML after use
      this.#cachedSectionHtml = null;
      this.#cachedSectionParams = null;
      
      this.#syncPaginationInfo();
      this.#restoreOpenFilters();
      this.#attachShowResultsListener();
      this.#attachLoadMoreListener();
      this.#attachCollapseFiltersListener();
      this.#attachSquadronSearchListeners();
      this.#setupDesktopStickyFilters();
      updateFilterButtonLabels();
      this.#logAppliedFilters('cache');
      this.#suppressViewTransition = false;
    };
    
    if (viewTransition) {
      await startViewTransition(updateDOM, ['product-grid']);
    } else {
      await updateDOM();
    }
  }

  /**
   * Closes the mobile filter drawer
   */
  #closeMobileFilters() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;
    
    const filtersWrapper = section.querySelector('.filters__wrapper');
    const overlay = section.querySelector('.filters__overlay-top');
    
    if (filtersWrapper) filtersWrapper.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  /**
   * Clears pending mobile filters (called when clearing all filters)
   */
  clearPendingMobileFilters() {
    this.#pendingMobileFilters = null;
    this.#cachedSectionHtml = null;
    this.#cachedSectionParams = null;
    this.#debouncedUpdateCountOnly.cancel();
    this.#abortPendingCountUpdate();
  }

  /**
   * Updates the section
   */
  #updateSection() {
    // Filters changed – any saved load-more state is now stale.
    sessionStorage.removeItem(this.#loadMoreStateKey);
    const viewTransition = this.#shouldUseViewTransition();

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']).then(() => {
        this.#syncPaginationInfo();
        this.#restoreOpenFilters();
        this.#attachShowResultsListener();
        this.#attachLoadMoreListener();
        this.#attachCollapseFiltersListener();
        this.#attachSquadronSearchListeners();
        this.#setupDesktopStickyFilters();
        updateFilterButtonLabels();
        this.#logAppliedFilters('render');
        this.#suppressViewTransition = false;
      });
    } else {
      sectionRenderer.renderSection(this.sectionId).then(() => {
        this.#syncPaginationInfo();
        this.#restoreOpenFilters();
        this.#attachShowResultsListener();
        this.#attachLoadMoreListener();
        this.#attachCollapseFiltersListener();
        this.#attachSquadronSearchListeners();
        this.#setupDesktopStickyFilters();
        updateFilterButtonLabels();
        this.#logAppliedFilters('render');
        this.#suppressViewTransition = false;
      });
    }
  }

  /**
   * Syncs pagination info with the rendered grid contents.
   */
  #syncPaginationInfo() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const grid = section.querySelector('#productGrid');
    const info = section.querySelector('#paginationInfo');
    if (!(grid instanceof HTMLElement) || !(info instanceof HTMLElement)) return;

    const renderedItems = Array.from(grid.children).filter((child) => child instanceof HTMLElement);
    if (renderedItems.length === 0) return;

    const rangeStart = Number(info.dataset.rangeStart || '1');
    const totalItems = Number(info.dataset.totalItems || '0');
    const rangeEndCandidate = Math.max(rangeStart, rangeStart + renderedItems.length - 1);
    const rangeEnd = totalItems ? Math.min(totalItems, rangeEndCandidate) : rangeEndCandidate;

    info.dataset.rangeEnd = String(rangeEnd);
    info.textContent = totalItems ? `${rangeStart}-${rangeEnd} of ${totalItems}` : `${rangeStart}-${rangeEnd}`;

    const loadMoreButton = section.querySelector('#loadMoreButton');
    if (!(loadMoreButton instanceof HTMLElement)) return;

    const currentPage = Number(loadMoreButton.dataset.currentPage || '1');
    const totalPages = Number(loadMoreButton.dataset.totalPages || '1');
    loadMoreButton.style.display = currentPage >= totalPages ? 'none' : '';
  }

  #attachLoadMoreListener() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const button = section.querySelector('#loadMoreButton');
    if (!(button instanceof HTMLElement)) return;

    // @ts-ignore - Custom property for event listener reference
    const existingListener = button._loadMoreListener;
    if (existingListener) {
      button.removeEventListener('click', existingListener);
    }

    const listener = async () => {
      const currentPage = Number(button.dataset.currentPage || '1');
      const totalPages = Number(button.dataset.totalPages || '1');
      if (currentPage >= totalPages || button.hasAttribute('disabled')) return;

      const nextPage = currentPage + 1;
      button.setAttribute('disabled', 'disabled');

      try {
        const params = this.createURLParameters();
        params.set('page', String(nextPage));

        const sectionUrl = this.#buildSectionRenderURL(params);
        const response = await fetch(sectionUrl);
        if (!response.ok) throw new Error(`Failed to load more products (status ${response.status}).`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const nextGrid = doc.querySelector('#productGrid');
        const currentGrid = section.querySelector('#productGrid');
        if (!(nextGrid instanceof HTMLElement) || !(currentGrid instanceof HTMLElement)) return;

        const nextItems = Array.from(nextGrid.children).filter((child) => child instanceof HTMLElement);
        if (!nextItems.length) return;

        const fragment = document.createDocumentFragment();
        nextItems.forEach((item) => fragment.appendChild(item.cloneNode(true)));
        currentGrid.appendChild(fragment);

        const nextButton = doc.querySelector('#loadMoreButton');
        button.dataset.currentPage = String(nextPage);
        if (nextButton instanceof HTMLElement && nextButton.dataset.totalPages) {
          button.dataset.totalPages = nextButton.dataset.totalPages;
        }

        this.#saveLoadMoreState(nextPage);

        const nextInfo = doc.querySelector('#paginationInfo');
        const currentInfo = section.querySelector('#paginationInfo');
        if (nextInfo instanceof HTMLElement && currentInfo instanceof HTMLElement) {
          if (nextInfo.dataset.totalItems) {
            currentInfo.dataset.totalItems = nextInfo.dataset.totalItems;
          }
        }

        this.#syncPaginationInfo();
        this.#forceDesktopCompact = true;
        this.#applyDesktopStickyState();
      } catch (error) {
        console.error('Load more failed:', error);
      } finally {
        button.removeAttribute('disabled');
      }
    };

    // @ts-ignore - Custom property for event listener reference
    button._loadMoreListener = listener;
    button.addEventListener('click', listener);
  }

  /**
   * Persists the highest page loaded + current scroll position so we can
   * replay "load more" fetches when the customer presses the back button.
   * @param {number} page
   */
  #saveLoadMoreState(page) {
    if (page <= 1) return;
    try {
      sessionStorage.setItem(
        this.#loadMoreStateKey,
        JSON.stringify({ page, scrollY: Math.round(window.scrollY), url: window.location.href })
      );
    } catch {}
  }

  /**
   * Keeps scrollY in the saved state up-to-date while the customer scrolls
   * through the already-expanded grid (debounced, passive).
   */
  #attachScrollStateSaver() {
    this.#scrollStateSaverHandler = debounce(() => {
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      if (!section) return;
      const button = section.querySelector('#loadMoreButton');
      if (!(button instanceof HTMLElement)) return;
      const currentPage = Number(button.dataset.currentPage || '1');
      if (currentPage <= 1) return;
      this.#saveLoadMoreState(currentPage);
    }, 300);
    window.addEventListener('scroll', this.#scrollStateSaverHandler, { passive: true });
  }

  /**
   * Re-fetches all pages that were loaded before the customer left, appends
   * them to the grid, and restores scroll position.
   *
   * Triggered on every connectedCallback. State is keyed by exact URL
   * (pathname + search), so stale state from a different collection or filter
   * set is ignored automatically.
   */
  async #restoreLoadMoreState() {
    let state;
    try {
      const raw = sessionStorage.getItem(this.#loadMoreStateKey);
      if (!raw) return;
      state = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(this.#loadMoreStateKey);
      return;
    }

    // Validate that saved state belongs to THIS exact collection + filter URL.
    let savedUrl;
    try {
      savedUrl = new URL(state.url, window.location.origin);
    } catch {
      sessionStorage.removeItem(this.#loadMoreStateKey);
      return;
    }

    const currentUrl = new URL(window.location.href);

    // Strip Shopify preview param from both URLs before comparing so the
    // feature works identically on the Dev Testing theme and on production.
    const normalize = (u) => {
      const p = new URLSearchParams(u.search);
      p.delete('preview_theme_id');
      p.sort();
      return u.pathname + (p.toString() ? '?' + p.toString() : '');
    };

    if (normalize(savedUrl) !== normalize(currentUrl)) {
      // Different collection or different active filters – leave state for
      // potential future back navigation, just don't restore now.
      return;
    }

    const targetPage = Number(state.page) || 1;
    if (targetPage <= 1) {
      sessionStorage.removeItem(this.#loadMoreStateKey);
      return;
    }

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const grid = section.querySelector('#productGrid');
    const button = section.querySelector('#loadMoreButton');
    if (!(grid instanceof HTMLElement) || !(button instanceof HTMLElement)) return;

    // Re-use the exact filter params that were active when the customer loaded more.
    const savedSearchParams = new URLSearchParams(savedUrl.search);
    savedSearchParams.delete('page');
    savedSearchParams.delete('preview_theme_id');

    for (let page = 2; page <= targetPage; page++) {
      try {
        const params = new URLSearchParams(savedSearchParams.toString());
        params.set('page', String(page));

        const fetchUrl = this.#buildSectionRenderURL(params);
        const response = await fetch(fetchUrl);
        if (!response.ok) break;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const nextGrid = doc.querySelector('#productGrid');
        if (!(nextGrid instanceof HTMLElement)) break;

        const items = Array.from(nextGrid.children).filter((el) => el instanceof HTMLElement);
        if (!items.length) break;

        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item.cloneNode(true)));
        grid.appendChild(fragment);

        button.dataset.currentPage = String(page);

        const nextBtn = doc.querySelector('#loadMoreButton');
        if (nextBtn instanceof HTMLElement && nextBtn.dataset.totalPages) {
          button.dataset.totalPages = nextBtn.dataset.totalPages;
        }
      } catch {
        break;
      }
    }

    this.#syncPaginationInfo();

    // Restore scroll after the browser has painted the restored items.
    const targetScrollY = Number(state.scrollY) || 0;
    setTimeout(() => {
      window.scrollTo({ top: targetScrollY, behavior: 'instant' });
    }, 50);

    sessionStorage.removeItem(this.#loadMoreStateKey);
  }

  #attachCollapseFiltersListener() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const buttons = section.querySelectorAll('[data-collapse-filters]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      // @ts-ignore - Custom property for event listener reference
      const existingListener = button._collapseFiltersListener;
      if (existingListener) {
        button.removeEventListener('click', existingListener);
      }

      const listener = () => {
        section.querySelectorAll('.filters__group[open]:not([data-persistent-open]), .filters__series-group[open]').forEach((details) => {
          if (details instanceof HTMLDetailsElement) {
            details.open = false;
          }
        });

        sessionStorage.removeItem(this.#openFiltersKey);
      };

      // @ts-ignore - Custom property for event listener reference
      button._collapseFiltersListener = listener;
      button.addEventListener('click', listener);
    });
  }

  #attachSquadronSearchListeners() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const searchInputs = section.querySelectorAll('[data-squadron-search]');
    if (!searchInputs.length) return;

    searchInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      // @ts-ignore - custom property for handler reference
      const existingListener = input._squadronSearchListener;
      if (existingListener) {
        input.removeEventListener('input', existingListener);
      }

      const listener = () => {
        const query = (input.value || '').trim().toLowerCase();
        const scope = input.closest('.filters__unit-subgroup, .filters__content, .filters__group') || section;
        const items = scope.querySelectorAll('[data-squadron-code-item]');

        items.forEach((item) => {
          if (!(item instanceof HTMLElement)) return;
          const label = item.dataset.squadronCodeLabel || item.textContent?.toLowerCase() || '';
          const shouldShow = query === '' || label.includes(query);
          item.style.display = shouldShow ? '' : 'none';
        });
      };

      // @ts-ignore - custom property for handler reference
      input._squadronSearchListener = listener;
      input.addEventListener('input', listener);
    });
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url, { preserveOpen = false } = {}) {
    const nextUrl = new URL(url, window.location.origin);
    history.pushState('', '', nextUrl.toString());
    this.dispatchEvent(new FilterUpdateEvent(nextUrl.searchParams));
    this.#storeOpenFilters();
    updateFilterButtonLabels(nextUrl.searchParams);
    if (preserveOpen) {
      this.#suppressViewTransition = true;
    }
    this.#updateSection();
  }

  #storeOpenFilters() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const openDetails = Array.from(section.querySelectorAll('.filters__group[open]'));
    const labels = openDetails
      .map((details) => details.querySelector('.filters__group-text')?.textContent?.trim())
      .filter(Boolean);

    if (!labels.length) {
      sessionStorage.removeItem(this.#openFiltersKey);
      return;
    }

    sessionStorage.setItem(this.#openFiltersKey, JSON.stringify(labels));
  }

  #restoreOpenFilters() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const raw = sessionStorage.getItem(this.#openFiltersKey);
    if (!raw) return;

    let labels = [];
    try {
      labels = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(this.#openFiltersKey);
      return;
    }

    if (!Array.isArray(labels) || !labels.length) return;

    const normalized = labels.map((label) => String(label).trim().toLowerCase()).filter(Boolean);
    if (!normalized.length) return;

    section.querySelectorAll('.filters__group').forEach((details) => {
      const text = details.querySelector('.filters__group-text')?.textContent?.trim().toLowerCase();
      if (text && normalized.includes(text)) {
        details.setAttribute('open', '');
      }
    });
  }

  #isDesktopViewport() {
    return window.innerWidth > 740;
  }

  #shouldUseViewTransition() {
    return false;
  }

  #setupDesktopStickyFilters() {
    this.#teardownDesktopStickyFilters();

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const filters = section.querySelector('.filters');
    if (!(filters instanceof HTMLElement)) return;

    this.#desktopCompactStickyEnabled = true;
    filters.classList.add('filters--sticky-enabled');

    this.#desktopScrollHandler = () => this.#applyDesktopStickyState();
    this.#desktopResizeHandler = () => this.#applyDesktopStickyState();

    window.addEventListener('scroll', this.#desktopScrollHandler, { passive: true });
    window.addEventListener('resize', this.#desktopResizeHandler);

    this.#applyDesktopStickyState();
  }

  #teardownDesktopStickyFilters() {
    if (this.#desktopScrollHandler) {
      window.removeEventListener('scroll', this.#desktopScrollHandler);
      this.#desktopScrollHandler = null;
    }
    if (this.#desktopResizeHandler) {
      window.removeEventListener('resize', this.#desktopResizeHandler);
      this.#desktopResizeHandler = null;
    }
    this.#desktopCompactStickyEnabled = false;
  }

  #applyDesktopStickyState() {
    if (!this.#desktopCompactStickyEnabled) return;

    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    const filters = section.querySelector('.filters');
    if (!(filters instanceof HTMLElement)) return;

    const shouldCompact = this.#isDesktopViewport() && this.#forceDesktopCompact;

    filters.classList.toggle('filters--compact', shouldCompact);

    if (this.#forceDesktopCompact && shouldCompact && !this.#isDesktopFiltersCompact) {
      this.#collapseDesktopFilterGroups(filters);
    }

    if (!this.#isDesktopViewport()) {
      this.#forceDesktopCompact = false;
    }

    this.#isDesktopFiltersCompact = shouldCompact;
  }

  #collapseDesktopFilterGroups(filtersElement) {
    const groups = filtersElement.querySelectorAll('.filters__group:not([data-persistent-open])');
    groups.forEach((group) => {
      if (group instanceof HTMLDetailsElement) {
        group.open = false;
      }
    });
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  /** @type {number | null} */
  #updateTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#updateSelectedFacetSummary();
  }

  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    if (this.#updateTimer) return;

    this.#updateTimer = setTimeout(() => {
      this.#updateTimer = null;
      facetsForm.updateFilters();
      this.#updateSelectedFacetSummary();
    }, 0);
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    const inputElement = event.target.querySelector('input');
    if (!form || !(inputElement instanceof HTMLInputElement)) return;

    const formData = new FormData(form);
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);
    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof HTMLElement)) return;
    if (typeof statusComponent.updateListSummary !== 'function') {
      customElements.whenDefined('facet-status-component').then(() => {
        this.#updateSelectedFacetSummary();
      });
      return;
    }

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updatePriceSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updatePriceSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (statusComponent instanceof FacetStatusComponent) {
      statusComponent.clearSummary();
    }

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) {
      facetsForm.updateFilters();
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey || event.key !== 'Enter') return;
    this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const container = facetsForm.closest('.shopify-section');
    if (container) {
      this.#clearAllFilterInputs(container);

      // Collapse all filter groups and sub-groups so they reset to closed state
      container.querySelectorAll('.filters__group[open]:not([data-persistent-open]), .filters__series-group[open]').forEach((details) => {
        details.removeAttribute('open');
      });
    }

    // Clear pending mobile filters when clearing all filters
    if (isMobile()) {
      facetsForm.clearPendingMobileFilters();
    }

    facetsForm.updateFiltersByURL(url, { preserveOpen: true });
  }

  /**
   * Clears all filter inputs in a container
   * @param {Element} container - The container element
   */
  #clearAllFilterInputs(container) {
    container.querySelectorAll('input[name^="filter."]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    // Always keep the sorting filter open
    if (!details.open) {
      details.open = true;
    }

    summary.setAttribute('aria-expanded', details.open.toString());

    if (details.open && listbox instanceof Element) {
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || !(option instanceof HTMLElement)) return;

    this.querySelectorAll('[role="option"]').forEach((opt) => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');

    input.click();
    this.#closeDropdown();
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;

    const options = this.querySelectorAll('[role="option"]');
    const selectedOption = this.querySelector('[aria-selected="true"]');

    options.forEach((opt) => {
      if (opt instanceof HTMLElement) opt.tabIndex = -1;
    });

    if (selectedOption instanceof HTMLElement) {
      selectedOption.tabIndex = 0;
    }

    if (summary instanceof HTMLElement) {
      summary.focus();
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const { details } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }

    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    this.#closeDropdown();
    this.#toggleInputsForMobile();

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    this.#toggleInputsForMobile(false);
  }

  /**
   * Toggles inputs based on mobile/desktop view
   * @param {boolean} [disable=true] - Whether to disable or enable
   */
  #toggleInputsForMobile(disable = true) {
    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';
    if (!shouldDisable) return;

    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      this.querySelectorAll('input[name="sort_by"]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.disabled = disable;
      });
    } else {
      const selectElement = this.querySelector('select[name="sort_by"]');
      if (selectElement instanceof HTMLSelectElement) selectElement.disabled = disable;
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof HTMLElement) || !('textContent' in facetStatus)) return;

    const isDefaultSort = event.target.value === details.dataset.defaultSortBy;
    facetStatus.textContent = isDefaultSort ? '' : (event.target.dataset.optionName ?? '');
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = checkedInputElements
      .map((inputElement) => inputElement.parentElement?.querySelector('span.swatch')?.outerHTML ?? '')
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      const formatConfig = this.#getFormatConfig(placeholder);
      return this.#formatCents(moneyValue, formatConfig.thousands, formatConfig.decimal, formatConfig.precision);
    });
  }

  /**
   * Gets format configuration for a placeholder
   * @param {string} placeholder - The placeholder name
   * @returns {{thousands: string, decimal: string, precision: number}} Format config
   */
  #getFormatConfig(placeholder) {
    const currency = this.refs.facetStatus.dataset.currency || '';
    let thousands = ',';
    let decimal = '.';
    let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

    switch (placeholder) {
      case 'amount_no_decimals':
        precision = 0;
        break;
      case 'amount_with_comma_separator':
        thousands = '.';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        thousands = '.';
        precision = 0;
        break;
      case 'amount_no_decimals_with_space_separator':
        thousands = ' ';
        precision = 0;
        break;
      case 'amount_with_space_separator':
        thousands = ' ';
        decimal = ',';
        break;
      case 'amount_with_period_and_space_separator':
        thousands = ' ';
        decimal = '.';
        break;
      case 'amount_with_apostrophe_separator':
        thousands = "'";
        decimal = '.';
        break;
    }

    return { thousands, decimal, precision };
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousands - The thousands separator
   * @param {string} decimal - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousands, decimal, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);
    let [whole, fraction] = roundedNumber.split('.');

    if (!whole) whole = '0';
    if (!fraction) fraction = '';

    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousands);

    return precision <= 0 ? whole : whole + decimal + fraction.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
